import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { 
  collection, 
  query, 
  where, 
  onSnapshot,
  getDocs,
  writeBatch
} from 'firebase/firestore';

export function useMessageNotifications() {
  const { currentUser } = useAuth();
  const [unreadCounts, setUnreadCounts] = useState({});
  const [loading, setLoading] = useState(false); // Start with false since we're not loading

  useEffect(() => {
    // Temporarily disable message notifications to isolate the Firestore assertion error
    setUnreadCounts({});
    setLoading(false);
    
    return () => {
      // Cleanup function
    };
  }, [currentUser?.uid]);

  // Mark messages as read for a specific order
  const markOrderAsRead = async (orderId) => {
    // Temporarily disabled to isolate Firestore assertion error
    console.log('Message notifications temporarily disabled');
  };

  // Get unread count for a specific order
  const getUnreadCount = (orderId) => {
    return unreadCounts[orderId] || 0;
  };

  // Get total unread count across all orders
  const getTotalUnreadCount = () => {
    return Object.values(unreadCounts).reduce((total, count) => total + count, 0);
  };

  return {
    unreadCounts,
    loading,
    markOrderAsRead,
    getUnreadCount,
    getTotalUnreadCount
  };
}

// Hook for managing notifications in the browser
export function useBrowserNotifications() {
  const [permission, setPermission] = useState('default');
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    // Check if browser supports notifications
    if ('Notification' in window) {
      setSupported(true);
      setPermission(Notification.permission);
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (!supported) return false;
    
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === 'granted';
    } catch (err) {
      console.error('Error requesting notification permission:', err);
      return false;
    }
  }, [supported]);

  const showNotification = useCallback((title, options = {}) => {
    if (!supported || permission !== 'granted') return false;
    
    try {
      const notification = new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        ...options
      });
      
      // Auto-close after 5 seconds
      setTimeout(() => notification.close(), 5000);
      
      return true;
    } catch (err) {
      console.error('Error showing notification:', err);
      return false;
    }
  }, [supported, permission]);

  return {
    supported,
    permission,
    requestPermission,
    showNotification,
    canShowNotifications: supported && permission === 'granted'
  };
}

// Hook for managing message sound notifications
export function useSoundNotifications() {
  const [audio, setAudio] = useState(null);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    // Create audio element for notification sounds
    const audioElement = new Audio('/notification-sound.mp3'); // You'll need to add this file
    audioElement.preload = 'auto';
    setAudio(audioElement);

    return () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.src = '';
      }
    };
  }, []);

  const playNotificationSound = useCallback(() => {
    if (!audio || !enabled) return;
    
    try {
      audio.currentTime = 0;
      audio.play().catch(err => {
        console.error('Error playing notification sound:', err);
      });
    } catch (err) {
      console.error('Error playing notification sound:', err);
    }
  }, [audio, enabled]);

  const toggleSound = useCallback(() => {
    setEnabled(prev => !prev);
  }, []);

  return {
    enabled,
    toggleSound,
    playNotificationSound
  };
}
